import type { Poem } from '@maria-cavalheiro-website/shared-types'
import { COLLECTIONS } from '@maria-cavalheiro-website/shared-types/config'

import { POEMS } from './poem-collection.ts'

/**
 * The four ways the index can be grouped.
 *
 * A pure module: no React, no hooks, no DOM. The design put all four groupings
 * inside the component class and rebuilt the active one on every render. Here they
 * are built once at import time and frozen, so switching modes is a Map probe.
 *
 * Every builder takes the poem list as a parameter rather than closing over the
 * module-level collection. That is not ceremony: it makes the edge cases — an
 * empty collection, a list with no undated poems — reachable from a test instead
 * of being defensive branches nothing can ever execute.
 */

export type GroupingMode = 'colecao' | 'tema' | 'ano' | 'az'

export const GROUPING_MODES: readonly GroupingMode[] = Object.freeze([
  'colecao',
  'tema',
  'ano',
  'az',
])

export const DEFAULT_GROUPING_MODE: GroupingMode = 'colecao'

export const GROUPING_MODE_LABELS: Record<GroupingMode, string> = Object.freeze({
  colecao: 'Coleção',
  tema: 'Tema',
  ano: 'Ano',
  az: 'Título',
})

export interface PoemGroup {
  readonly id: string
  readonly label: string
  /** `5 poemas` — or `de A a Z` for the single alphabetical group. */
  readonly note: string
  /** Advisory shown under the group heading. Only the undated group has one. */
  readonly advisory?: string
  readonly poems: readonly Poem[]
}

const LOCALE = 'pt-BR'
const UNDATED_GROUP_ID = 'sem-data'
const UNDATED_LABEL = 'Sem data'
const NO_POEMS: readonly Poem[] = Object.freeze([])

/**
 * The design's advisory here read as an instruction to the site's author ("Basta
 * informar o ano de cada poema…"). On a published site the reader is the audience,
 * so it states the fact instead.
 *
 * The word "Dois" is tied to the real collection. `poem-collection.test.ts` asserts
 * that exactly two poems are undated, which is what keeps this copy honest.
 */
const UNDATED_ADVISORY = 'Dois poemas ainda estão sem data.'

function pluralise(count: number): string {
  return `${String(count)} ${count === 1 ? 'poema' : 'poemas'}`
}

/**
 * One pass, O(n). Never `filter` inside `map` — that is the O(n²) shape
 * rules/architecture.md calls out by name, and the design does it twice.
 */
function bucket<T, K>(items: readonly T[], keyOf: (item: T) => K): Map<K, T[]> {
  const buckets = new Map<K, T[]>()

  for (const item of items) {
    const key = keyOf(item)
    const existing = buckets.get(key)

    if (existing === undefined) {
      buckets.set(key, [item])
    } else {
      existing.push(item)
    }
  }

  return buckets
}

function byCollection(poems: readonly Poem[]): readonly PoemGroup[] {
  const buckets = bucket(poems, (poem) => poem.collection)

  return COLLECTIONS.map((collection) => {
    const grouped = buckets.get(collection.id) ?? NO_POEMS

    return {
      id: `colecao-${String(collection.id)}`,
      label: collection.label,
      note: pluralise(grouped.length),
      poems: Object.freeze(grouped),
    }
  })
}

/**
 * Descending by count, ties broken by pt-BR collation.
 *
 * With the real content that is Natureza 9, then Casa 5 and Pássaros 5 — where the
 * comparator actually earns its keep, since `Casa` precedes `Pássaros` only if the
 * locale tie-break is wired up — then Memória 4.
 */
function byTheme(poems: readonly Poem[]): readonly PoemGroup[] {
  return [...bucket(poems, (poem) => poem.theme).entries()]
    .sort(([themeA, a], [themeB, b]) => b.length - a.length || themeA.localeCompare(themeB, LOCALE))
    .map(([theme, grouped]) => ({
      id: `tema-${theme}`,
      label: theme,
      note: pluralise(grouped.length),
      poems: Object.freeze(grouped),
    }))
}

/**
 * Descending by year, with undated poems partitioned out and appended last.
 *
 * Partitioning rather than sorting a comparator over `string | undefined`: the
 * undefined arms of such a comparator are reached or not depending on the engine's
 * sort order, which is not something to leave a conformance property resting on.
 *
 * A schema-valid poem has a well-formed `publishedAt` or none at all, so the
 * absence check is exactly the "date unknown" test — no parsing required.
 */
function byYear(poems: readonly Poem[]): readonly PoemGroup[] {
  // The year is captured during the partition, not re-derived afterwards: a second
  // pass would have to re-test `publishedAt` for a narrowing the compiler has
  // already lost, and that defensive operator is a branch no test could reach.
  const dated: { readonly year: string; readonly poem: Poem }[] = []
  const undated: Poem[] = []

  for (const poem of poems) {
    if (poem.publishedAt === undefined) {
      undated.push(poem)
    } else {
      dated.push({ year: poem.publishedAt.slice(0, 4), poem })
    }
  }

  const yearGroups = [...bucket(dated, (entry) => entry.year).entries()]
    .sort(([yearA], [yearB]) => yearB.localeCompare(yearA, LOCALE))
    .map(([year, entries]) => ({
      id: `ano-${year}`,
      label: year,
      note: pluralise(entries.length),
      poems: Object.freeze(entries.map((entry) => entry.poem)),
    }))

  if (undated.length === 0) {
    return yearGroups
  }

  return [
    ...yearGroups,
    {
      id: UNDATED_GROUP_ID,
      label: UNDATED_LABEL,
      note: pluralise(undated.length),
      advisory: UNDATED_ADVISORY,
      poems: Object.freeze(undated),
    },
  ]
}

/**
 * `localeCompare`, not `<`.
 *
 * `Á` sorts after `A` and before `B` in pt-BR, but after `Z` by code point — so
 * `As Árvores` would land in the wrong place with a naive comparison.
 */
function alphabetically(poems: readonly Poem[]): readonly PoemGroup[] {
  const sorted = [...poems].sort((a, b) => a.title.localeCompare(b.title, LOCALE))

  return [
    {
      id: 'az',
      label: 'Todos os poemas',
      note: 'de A a Z',
      poems: Object.freeze(sorted),
    },
  ]
}

const BUILDERS: Record<GroupingMode, (poems: readonly Poem[]) => readonly PoemGroup[]> = {
  colecao: byCollection,
  tema: byTheme,
  ano: byYear,
  az: alphabetically,
}

/**
 * The pure core: grouping as a function of a poem list.
 *
 * Exported for tests, and because it documents that a third collection needs
 * nothing here beyond a `COLLECTIONS` entry.
 */
export function buildPoemGroups(
  poems: readonly Poem[],
  mode: GroupingMode,
): readonly PoemGroup[] {
  const build = BUILDERS[mode] as ((poems: readonly Poem[]) => readonly PoemGroup[]) | undefined

  return build === undefined ? NO_GROUPS : Object.freeze(build(poems))
}

const NO_GROUPS: readonly PoemGroup[] = Object.freeze([])

/**
 * Built once at module load. 23 poems x 4 modes is trivial work done at import
 * time; switching modes is then a Map probe with zero recomputation on the
 * interaction path.
 */
const GROUPS_BY_MODE: ReadonlyMap<GroupingMode, readonly PoemGroup[]> = new Map(
  GROUPING_MODES.map((mode) => [mode, buildPoemGroups(POEMS, mode)]),
)

/** O(1), and the same frozen array identity every time — so `useMemo` is pointless here. */
export function poemGroups(mode: GroupingMode): readonly PoemGroup[] {
  return GROUPS_BY_MODE.get(mode) ?? NO_GROUPS
}

/** `Agrupado por tema · 4 grupos` — announced when the reader switches mode. */
export function groupingAnnouncement(mode: GroupingMode): string {
  const count = poemGroups(mode).length
  const groupWord = count === 1 ? 'grupo' : 'grupos'
  const label = GROUPING_MODE_LABELS[mode] as string | undefined

  return `Agrupado por ${(label ?? '').toLocaleLowerCase(LOCALE)} · ${String(count)} ${groupWord}`
}
